import { useState, useCallback, useMemo } from 'react';

/**
 * Navigation views for the Zero Trust Journey
 */
export const JOURNEY_VIEWS = {
  REFERENCE: 'REFERENCE',       // 5-pillar overview
  PILLAR: 'PILLAR',             // Functions within a pillar
  FUNCTION: 'FUNCTION',         // Maturity levels for a function
  LEVEL: 'LEVEL',               // Single level diagram view
  ASSESSMENT: 'ASSESSMENT',     // Where am I? / Where do I want to be?
  GAP_ANALYSIS: 'GAP_ANALYSIS'  // Current vs target comparison
};

/**
 * Assessment phases
 */
export const ASSESSMENT_PHASES = {
  CURRENT: 'CURRENT',   // Selecting current maturity level
  TARGET: 'TARGET'      // Selecting target maturity level
};

/**
 * Initial navigation state
 */
const INITIAL_STATE = {
  view: JOURNEY_VIEWS.REFERENCE,
  selectedPillar: null,
  selectedFunction: null,
  selectedLevel: null,
  assessmentPhase: null,
  history: []
};

/**
 * Hook for managing Zero Trust Journey navigation
 *
 * Provides state machine for navigating through:
 * Reference Architecture → Pillar → Function → Level/Assessment → Gap Analysis
 */
export function useJourneyNavigation() {
  const [state, setState] = useState(INITIAL_STATE);

  /**
   * Push current state to history before navigating
   */
  const pushHistory = useCallback((currentState) => {
    return [...currentState.history, {
      view: currentState.view,
      selectedPillar: currentState.selectedPillar,
      selectedFunction: currentState.selectedFunction,
      selectedLevel: currentState.selectedLevel,
      assessmentPhase: currentState.assessmentPhase
    }];
  }, []);

  /**
   * Navigate to pillar drilldown
   */
  const selectPillar = useCallback((pillarCode) => {
    setState(prev => ({
      ...prev,
      view: JOURNEY_VIEWS.PILLAR,
      selectedPillar: pillarCode,
      selectedFunction: null,
      selectedLevel: null,
      assessmentPhase: null,
      history: pushHistory(prev)
    }));
  }, [pushHistory]);

  /**
   * Navigate to function maturity view
   */
  const selectFunction = useCallback((functionCode) => {
    setState(prev => ({
      ...prev,
      view: JOURNEY_VIEWS.FUNCTION,
      selectedFunction: functionCode,
      selectedLevel: null,
      assessmentPhase: null,
      history: pushHistory(prev)
    }));
  }, [pushHistory]);

  /**
   * View a specific maturity level diagram
   */
  const viewLevel = useCallback((level) => {
    setState(prev => ({
      ...prev,
      view: JOURNEY_VIEWS.LEVEL,
      selectedLevel: level,
      assessmentPhase: null,
      history: pushHistory(prev)
    }));
  }, [pushHistory]);

  /**
   * Start assessment flow
   */
  const startAssessment = useCallback((phase = ASSESSMENT_PHASES.CURRENT) => {
    setState(prev => ({
      ...prev,
      view: JOURNEY_VIEWS.ASSESSMENT,
      assessmentPhase: phase,
      history: pushHistory(prev)
    }));
  }, [pushHistory]);

  /**
   * Move to target selection in assessment
   */
  const selectTargetPhase = useCallback(() => {
    setState(prev => ({
      ...prev,
      assessmentPhase: ASSESSMENT_PHASES.TARGET
    }));
  }, []);

  /**
   * Navigate to gap analysis view
   */
  const viewGapAnalysis = useCallback(() => {
    setState(prev => ({
      ...prev,
      view: JOURNEY_VIEWS.GAP_ANALYSIS,
      assessmentPhase: null,
      history: pushHistory(prev)
    }));
  }, [pushHistory]);

  /**
   * Go back one step in navigation history
   */
  const goBack = useCallback(() => {
    setState(prev => {
      if (prev.history.length === 0) {
        return INITIAL_STATE;
      }

      const newHistory = [...prev.history];
      const previousState = newHistory.pop();

      return {
        ...previousState,
        history: newHistory
      };
    });
  }, []);

  /**
   * Reset to initial state
   */
  const reset = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  /**
   * Navigate directly to a specific view with params
   */
  const navigateTo = useCallback((view, params = {}) => {
    setState(prev => ({
      ...prev,
      view,
      ...params,
      history: pushHistory(prev)
    }));
  }, [pushHistory]);

  /**
   * Get breadcrumb trail for current navigation state
   */
  const breadcrumbs = useMemo(() => {
    const crumbs = [
      { id: 'reference', label: 'Reference Architecture', view: JOURNEY_VIEWS.REFERENCE }
    ];

    if (state.selectedPillar) {
      crumbs.push({
        id: 'pillar',
        label: state.selectedPillar,
        view: JOURNEY_VIEWS.PILLAR
      });
    }

    if (state.selectedFunction) {
      crumbs.push({
        id: 'function',
        label: state.selectedFunction,
        view: JOURNEY_VIEWS.FUNCTION
      });
    }

    if (state.view === JOURNEY_VIEWS.LEVEL && state.selectedLevel) {
      crumbs.push({
        id: 'level',
        label: state.selectedLevel,
        view: JOURNEY_VIEWS.LEVEL
      });
    }

    if (state.view === JOURNEY_VIEWS.ASSESSMENT) {
      crumbs.push({
        id: 'assessment',
        label: state.assessmentPhase === ASSESSMENT_PHASES.CURRENT
          ? 'Current State'
          : 'Target State',
        view: JOURNEY_VIEWS.ASSESSMENT
      });
    }

    if (state.view === JOURNEY_VIEWS.GAP_ANALYSIS) {
      crumbs.push({
        id: 'gap',
        label: 'Gap Analysis',
        view: JOURNEY_VIEWS.GAP_ANALYSIS
      });
    }

    return crumbs;
  }, [state]);

  /**
   * Navigate via breadcrumb click
   */
  const navigateToBreadcrumb = useCallback((crumbId) => {
    switch (crumbId) {
      case 'reference':
        reset();
        break;
      case 'pillar':
        setState(prev => ({
          ...prev,
          view: JOURNEY_VIEWS.PILLAR,
          selectedFunction: null,
          selectedLevel: null,
          assessmentPhase: null,
          history: []
        }));
        break;
      case 'function':
        setState(prev => ({
          ...prev,
          view: JOURNEY_VIEWS.FUNCTION,
          selectedLevel: null,
          assessmentPhase: null,
          history: prev.history.filter(h =>
            h.view === JOURNEY_VIEWS.REFERENCE ||
            h.view === JOURNEY_VIEWS.PILLAR
          )
        }));
        break;
      default:
        break;
    }
  }, [reset]);

  /**
   * Check if can go back
   */
  const canGoBack = state.history.length > 0 || state.view !== JOURNEY_VIEWS.REFERENCE;

  return {
    // Current state
    ...state,

    // Navigation actions
    selectPillar,
    selectFunction,
    viewLevel,
    startAssessment,
    selectTargetPhase,
    viewGapAnalysis,
    goBack,
    reset,
    navigateTo,
    navigateToBreadcrumb,

    // Derived state
    breadcrumbs,
    canGoBack,

    // View constants
    JOURNEY_VIEWS,
    ASSESSMENT_PHASES
  };
}

export default useJourneyNavigation;
